import { useEffect, useState, useCallback } from 'react';
import { Table, Pagination, Row, Cell, Button, Input, MenuItem, MenuHeader, Dropdown, Select, Tag, Dialog } from '@dsi/react-eds';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { useLoader } from '../../../hooks/useLoader';
import { useWebSocketService } from '../../../hooks/useWebSocketService';
import socketService from '../../../services/WebsocketService';
import { useDebounce } from '../../../hooks/useDebounce';
import { gnbNodesService, GnbNode } from '../../../services/gnbNodesService';
import CiqPreviewDialog from '../../Pipeline/CiqPreviewDialog';
import styles from './GnbNodes.module.css';

const GnbNodesList = () => {
  const [nodes, setNodes] = useState<GnbNode[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<{ nodeName?: string; nodeIp?: string; environment?: string; controlPlane?: string; controlPlaneEndpoint?: string; source?: string; referenceId?: string }>({});
  const debouncedFilters = useDebounce(filters, 500);
  const [showFilters, setShowFilters] = useState(false);
  const [previewNode, setPreviewNode] = useState<GnbNode | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; node: GnbNode | null }>({ open: false, node: null });
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();
  useWebSocketService();
  const [limit, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(undefined);

  const sortableColumns = ['nodeName', 'nodeIp', 'environment', 'controlPlane', 'controlPlaneEndpoint', 'source', 'status', 'referenceId', 'currentRelease', 'lastSyncedAt'];
  const columns = [
    { title: 'Node Name', attribute: 'nodeName' },
    { title: 'IP', attribute: 'nodeIp' },
    { title: 'Environment', attribute: 'environment' },
    { title: 'Control Plane', attribute: 'controlPlane' },
    { title: 'Endpoint', attribute: 'controlPlaneEndpoint' },
    { title: 'Source', attribute: 'source' },
    { title: 'Status', attribute: 'status' },
    { title: 'Blueprint', attribute: 'referenceId' },
    { title: 'Current Release', attribute: 'currentRelease' },
    { title: 'CIQ', attribute: 'ciq' },
    { title: 'Config (Day-1)', attribute: 'day1' },
    { title: 'Last Synced', attribute: 'lastSyncedAt' }
  ].map((col) => ({
    ...col,
    ...(sortableColumns.includes(col.attribute) ? { sortable: true, sortOrder: sortBy === col.attribute ? sortOrder : undefined } : {}),
  }));

  const handleDownloadCiq = async (node: GnbNode) => {
    try {
      const res = await gnbNodesService.downloadCiq(node.id);
      const blob = new Blob([res.data], { type: 'application/x-yaml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ciq_${node.nodeName}.yaml`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      addNotifications([{
        title: 'Error',
        icon: 'icon-triangle-warning',
        description: `Failed to download CIQ for ${node.nodeName}: ${err instanceof Error ? err.message : 'Unknown error'}`,
        color: 'red',
        timestamp: new Date()
      }]);
    }
  };

  const handlePreview = (node: GnbNode) => setPreviewNode(node);

  const onGeneralFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      showLoader();
      const payload = await Promise.all(files.map(async (f) => ({ name: f.name, content: await f.text() })));
      const { data } = await gnbNodesService.uploadCiqGeneral(payload);
      fetchNodes();
      if (data.errors?.length) {
        addNotifications([{ title: 'Upload Failed', icon: 'icon-triangle-warning', description: data.errors.map((err: { filename: string; error: string }) => `${err.filename}: ${err.error}`).join('; '), color: 'red', timestamp: new Date() }]);
      }
      if (data.results?.length) {
        addNotifications([{ title: 'Success', icon: 'icon-check', description: `${data.results.length} node(s) uploaded`, color: 'green', timestamp: new Date() }]);
      }
    } catch (err) {
      addNotifications([{ title: 'Error', icon: 'icon-triangle-warning', description: `Failed to upload CIQ: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'red', timestamp: new Date() }]);
    } finally {
      setTimeout(() => hideLoader(), 500);
      e.target.value = '';
    }
  };

  const handleDeleteNode = (node: GnbNode) => {
    setDeleteConfirm({ open: true, node });
  };

  const confirmDeleteNode = async () => {
    const node = deleteConfirm.node;
    setDeleteConfirm({ open: false, node: null });
    if (!node) return;
    try {
      await gnbNodesService.delete(node.id);
      addNotifications([{ title: 'Success', icon: 'icon-check', description: `Node ${node.nodeName} deleted`, color: 'green', timestamp: new Date() }]);
      fetchNodes();
    } catch (err) {
      addNotifications([{ title: 'Error', icon: 'icon-triangle-warning', description: `Failed to delete ${node.nodeName}: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'red', timestamp: new Date() }]);
    }
  };

  const fetchNodes = useCallback(async () => {
    setLoading(true);
    showLoader();
    try {
      const res = await gnbNodesService.getAll({ page, limit, ...debouncedFilters, sortBy, sortOrder });
      const responseData = res.data;
      setNodes(Array.isArray(responseData) ? responseData : responseData.data || []);
      setTotal(Array.isArray(responseData) ? responseData.length : responseData.total || 0);
    } catch (err) {
      addNotifications([{
        title: 'Error',
        icon: 'icon-triangle-warning',
        description: `Failed to fetch gNB nodes: ${err instanceof Error ? err.message : 'Unknown error'}`,
        color: 'red',
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
      setTimeout(() => hideLoader(), 500);
    }
  }, [addNotifications, page, limit, debouncedFilters, sortBy, sortOrder]);

  useEffect(() => {
    fetchNodes();
  }, [fetchNodes]);

  useEffect(() => {
    const unsubscribe = socketService.onEntityChange('GNB_NODE', (event) => {
      const { action, payload } = event;
      if (action === 'delete') {
        setNodes((prev) => prev.filter((n) => n.id !== payload.id));
        setTotal((t) => Math.max(0, t - 1));
      } else if (action === 'create') {
        setTotal((t) => t + 1);
      } else {
        setNodes((prev) => prev.map((n) => n.id === payload.id ? { ...n, ...payload } : n));
      }
    });
    return unsubscribe;
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      await gnbNodesService.triggerSync();
    } catch {
      // sync trigger may fail — still fetch local nodes
    }
    fetchNodes();
  };

  const getStatusBadge = (status: string) => {
    const colorMap: Record<string, string> = {
      new: 'blue',
      deployed: 'green',
      provisioned: 'green',
      upgrading: 'orange',
      failed: 'red'
    };
    return <ReactEDSWrapper component={Tag} color={colorMap[status] || 'blue'}>{status}</ReactEDSWrapper>;
  };

  const numPages = Math.ceil(total / limit);

  return (
    <div>
      <input type="file" id="ciq-file-input-general" accept=".yaml,.yml" multiple style={{ display: 'none' }} onChange={onGeneralFileSelected} />
      <div className="table-top">
        <div className="table-top-left">
          <div className="table-info">
            <span>
              {total > 0
                ? `${(page - 1) * limit + 1} - ${Math.min(page * limit, total)} of `
                : '0 of '}
            </span>
            <span>{total} nodes</span>
          </div>
        </div>
        <div className="table-top-right">
          <div className="table-buttons">
            <ReactEDSWrapper component={Button} icon="refresh" onClick={handleRefresh}>
              Sync Nodes
            </ReactEDSWrapper>
            <ReactEDSWrapper component={Button} icon={showFilters ? 'filter-off' : 'filter'} onClick={() => setShowFilters(!showFilters)}>
              {showFilters ? 'Close Filters' : 'Filters'}
            </ReactEDSWrapper>
            {showFilters && (
              <ReactEDSWrapper component={Button} icon="filter-clear" onClick={() => { setFilters({}); setPage(1); }}>
                Clear Filters
              </ReactEDSWrapper>
            )}
          </div>
        </div>
      </div>

      <ReactEDSWrapper
        component={Table}
        id="gnb-nodes-table"
        columns={columns}
        rowActions
        sortable
        onSort={(order: 'asc' | 'desc', attr: string) => { setSortBy(attr); setSortOrder(order); setPage(1); }}
        filters={
          <ReactEDSWrapper component={Row} key="filter-row">
            {columns.map((column) => (
              <ReactEDSWrapper key={`filter-${column.attribute}`} component={Cell}>
                {column.attribute === 'controlPlane' ? (
                  <ReactEDSWrapper component={Select} label="All" value={filters.controlPlane || ''} onChange={(event: any) => { const val = event?.target?.value ?? event; setFilters((prev) => ({ ...prev, controlPlane: val || undefined })); setPage(1); }}>
                    <ReactEDSWrapper component={MenuItem} value="">All</ReactEDSWrapper>
                    <ReactEDSWrapper component={MenuItem} value="RLO">RLO</ReactEDSWrapper>
                    <ReactEDSWrapper component={MenuItem} value="ENM">ENM</ReactEDSWrapper>
                    <ReactEDSWrapper component={MenuItem} value="none">None</ReactEDSWrapper>
                  </ReactEDSWrapper>
                ) : column.attribute === 'source' ? (
                  <ReactEDSWrapper component={Select} label="All" value={filters.source || ''} onChange={(event: any) => { const val = event?.target?.value ?? event; setFilters((prev) => ({ ...prev, source: val || undefined })); setPage(1); }}>
                    <ReactEDSWrapper component={MenuItem} value="">All</ReactEDSWrapper>
                    <ReactEDSWrapper component={MenuItem} value="sync">sync</ReactEDSWrapper>
                    <ReactEDSWrapper component={MenuItem} value="manual">manual</ReactEDSWrapper>
                  </ReactEDSWrapper>
                ) : ['nodeName', 'nodeIp', 'environment', 'controlPlaneEndpoint', 'referenceId'].includes(column.attribute) ? (
                  <ReactEDSWrapper
                    component={Input}
                    id={`${column.attribute}_filter`}
                    placeholder="Filter"
                    fullwidth
                    value={(filters as any)[column.attribute] || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const val = e.target.value.trim();
                      setFilters((prev) => ({ ...prev, [column.attribute]: val || undefined }));
                      setPage(1);
                    }}
                  />
                ) : null}
              </ReactEDSWrapper>
            ))}
          </ReactEDSWrapper>
        }
        showFilters={showFilters}
      >
        {loading ? (
          <ReactEDSWrapper component={Row} key="loading">
            <ReactEDSWrapper component={Cell} colSpan={columns.length} key="loading-cell">
              <div className={styles.noDataContainer}>Loading...</div>
            </ReactEDSWrapper>
          </ReactEDSWrapper>
        ) : nodes.length === 0 ? (
          <ReactEDSWrapper component={Row} key="no-data">
            <ReactEDSWrapper component={Cell} colSpan={columns.length} key="no-data-cell">
              <div className={styles.noDataContainer}>
                <i className="icon icon-info" />
                <div className={styles.noDataTitle}>No gNB Nodes Found</div>
                <div className={styles.noDataSubtitle}>Sync from RLO or upload CIQ to add nodes.</div>
              </div>
            </ReactEDSWrapper>
          </ReactEDSWrapper>
        ) : (
          nodes.map((node) => (
            <ReactEDSWrapper
              component={Row}
              key={node.id}
              actions={[
                <ReactEDSWrapper component={Dropdown} more key={`menu-${node.id}`} className="wf-con-menu">
                  {[
                    ...(node.currentDraftName ? [<ReactEDSWrapper key="preview" component={MenuItem} onClick={() => handlePreview(node)}>Preview</ReactEDSWrapper>] : []),
                    ...(node.currentDraftName ? [<hr key="sep1" />, <ReactEDSWrapper key="header-download" component={MenuHeader}>Download</ReactEDSWrapper>] : []),
                    ...(node.currentDraftName ? [<ReactEDSWrapper key="download-active" component={MenuItem} onClick={() => handleDownloadCiq(node)}>{node.status === 'deployed' ? 'Active CIQ' : 'In-Draft CIQ'}</ReactEDSWrapper>] : []),
                    <hr key="sep2" />,
                    <ReactEDSWrapper key="delete" component={MenuItem} icon="trashcan" onClick={() => handleDeleteNode(node)}>Delete Node</ReactEDSWrapper>,
                  ]}
                </ReactEDSWrapper>
              ]}
            >
              <ReactEDSWrapper component={Cell}>{node.nodeName}</ReactEDSWrapper>
              <ReactEDSWrapper component={Cell}>{node.nodeIp || '—'}</ReactEDSWrapper>
              <ReactEDSWrapper component={Cell}>{node.environment || '—'}</ReactEDSWrapper>
              <ReactEDSWrapper component={Cell}>{node.controlPlane}</ReactEDSWrapper>
              <ReactEDSWrapper component={Cell}>{node.controlPlaneEndpoint || '—'}</ReactEDSWrapper>
              <ReactEDSWrapper component={Cell}>{node.source || '—'}</ReactEDSWrapper>
              <ReactEDSWrapper component={Cell}>{getStatusBadge(node.status)}</ReactEDSWrapper>
              <ReactEDSWrapper component={Cell}>{node.referenceId ? node.referenceId.replace('vendor-blueprints.eric-cloud-5g-ran.', '') : '—'}</ReactEDSWrapper>
              <ReactEDSWrapper component={Cell}>{node.currentRelease || '—'}</ReactEDSWrapper>
              <ReactEDSWrapper component={Cell}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '12px' }}>
                  {node.currentDraftName && (
                    <ReactEDSWrapper component={Tag} color={node.status === 'deployed' ? 'green' : 'blue'}>{node.status === 'deployed' ? 'Active' : 'In Draft'}</ReactEDSWrapper>
                  )}
                  {node.hasStagedCiq && (
                    <ReactEDSWrapper component={Tag} color="orange">Staged</ReactEDSWrapper>
                  )}
                  {!node.currentDraftName && !node.hasStagedCiq && '—'}
                </div>
              </ReactEDSWrapper>
              <ReactEDSWrapper component={Cell}>
                {node.hasStagedDay1 ? (
                  <ReactEDSWrapper component={Tag} color="orange">Staged</ReactEDSWrapper>
                ) : '—'}
              </ReactEDSWrapper>
              <ReactEDSWrapper component={Cell}>
                {node.lastSyncedAt ? new Date(node.lastSyncedAt).toLocaleString() : '—'}
              </ReactEDSWrapper>
            </ReactEDSWrapper>
          ))
        )}
      </ReactEDSWrapper>

      {total > 0 && (
        <ReactEDSWrapper
          component={Pagination}
          numPages={numPages || 1}
          selectedPage={page}
          onPaginationChangePage={setPage}
          optionsList={[
            { value: '20', label: '20', active: limit === 20 },
            { value: '50', label: '50', active: limit === 50 },
            { value: '100', label: '100', active: limit === 100 },
          ]}
          onPaginationChangeSelect={(size: string) => { setPageSize(Number(size)); setPage(1); }}
          withActions
        />
      )}

      {previewNode && (
        <CiqPreviewDialog node={previewNode} onClose={() => setPreviewNode(null)} />
      )}
      {deleteConfirm.open && (
        <ReactEDSWrapper
          component={Dialog}
          show
          title="Delete Node"
          onClose={() => setDeleteConfirm({ open: false, node: null })}
          bottom={<ReactEDSWrapper component={Button} warning onClick={confirmDeleteNode}>Delete Node</ReactEDSWrapper>}
        >
          <p>Are you sure you want to delete <strong>{deleteConfirm.node?.nodeName}</strong>? This action cannot be undone.</p>
        </ReactEDSWrapper>
      )}
    </div>
  );
};

export default GnbNodesList;
